import React, { useMemo } from "react";

import { Popover, usePopoverContext } from "@aircall/tractor";
import { debounce } from "lodash";

import {
  SentimentTooltipContentProps,
  SentimentTooltipProps,
  SentimentTooltipTriggerProps,
} from "./SentimentTooltip.decl";
import { BubbleContainer } from "./SentimentTooltip.styled";

import NegativeSvg from "@/assets/negative.svg?react";
import PositiveSvg from "@/assets/positive.svg?react";

function SentimentTooltipContent({ value }: SentimentTooltipContentProps) {
  switch (value) {
    case "POSITIVE":
      return <PositiveSvg />;
    case "NEGATIVE":
      return <NegativeSvg />;
    default:
      return null;
  }
}

function TooltipTrigger({
  children,
  mouseEnterDelay = 0,
  mouseLeaveDelay = 100,
}: SentimentTooltipTriggerProps) {
  const { setOpen } = usePopoverContext();
  const handleClose = useMemo(
    () =>
      debounce(() => {
        setOpen(false);
      }, mouseLeaveDelay),
    [setOpen, mouseLeaveDelay]
  );

  const handleOpen = useMemo(
    () =>
      debounce(() => {
        handleClose.cancel();
        setOpen(true);
      }, mouseEnterDelay),
    [handleClose, setOpen, mouseEnterDelay]
  );

  return (
    <Popover.Trigger onMouseEnter={handleOpen} onMouseLeave={handleClose}>
      {children}
    </Popover.Trigger>
  );
}

export function SentimentTooltip({
  children,
  mouseEnterDelay,
  mouseLeaveDelay,
  value,
}: SentimentTooltipProps) {
  return (
    <Popover.Root>
      <TooltipTrigger
        mouseEnterDelay={mouseEnterDelay}
        mouseLeaveDelay={mouseLeaveDelay}
      >
        {React.Children.only(children)}
      </TooltipTrigger>
      <Popover.Content align="center" side="top">
        <BubbleContainer value={value}>
          <SentimentTooltipContent value={value} />
        </BubbleContainer>
      </Popover.Content>
    </Popover.Root>
  );
}
